// ============================================================
// ClientDeletedDataTab.js
// Deleted Data tab inside <ClientDetailPage>. A2Z IT-only
// surface for the platform-wide two-stage deletion system —
// list every soft-deleted + client-permanent-deleted entity for
// one Client; restore back to live state OR physically delete
// (with cascade + blob cleanup).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-list.js
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-restore.js
//   backend/func-carehub-api/src/functions/admin-client-deleted-data-hard-delete.js
//
// Notes:
//   - Both Restore and Hard Delete write a fresh DeletionLog row
//     server-side (DeletionLog is append-only); the list reloads
//     after every mutation so the currentState chip updates.
//   - admin-restore IS blocked server-side after admin_hard_delete
//     (the entity row no longer exists; the helper 404s). The
//     client guards via currentState too — Restore is hidden on
//     admin_hard_deleted rows.
//   - Hard Delete is irreversible by design — confirm dialog is
//     red + double-confirm.
//   - includeClientPermanentDeleted is on by default. Operators
//     need to see what clients have hidden, not just what was
//     soft-deleted.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import JsonDialog from '../components/JsonDialog';
import {
  listDeletedData, restoreDeletedEntity, hardDeleteEntity,
} from '../api/adminDeletedData';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';
import './ClientDeletedDataTab.css';

const ENTITY_TYPE_OPTIONS = [
  { value: '',                   label: 'All entity types' },
  { value: 'governance_event',   label: 'CareTrack — Governance Events' },
  { value: 'compliance_policy',  label: 'CareComply — Policies' },
  { value: 'training_record',    label: 'CareComply — Training Records' },
  { value: 'audit_instance',     label: 'CareAudit — Audit Instances' },
  { value: 'survey_contact',     label: 'CareVoice — Survey Contacts' },
];

const ENTITY_TYPE_LABEL = ENTITY_TYPE_OPTIONS.reduce((acc, o) => {
  if (o.value) acc[o.value] = o.label;
  return acc;
}, {});

const STATE_BADGE = {
  soft_deleted:             { variant: 'warning', label: 'Soft-deleted (client)' },
  client_permanent_deleted: { variant: 'warning', label: 'Permanent (client view)' },
  admin_hard_deleted:       { variant: 'danger',  label: 'Hard-deleted (A2Z IT)' },
  restored:                 { variant: 'success', label: 'Restored' },
  unknown:                  { variant: 'neutral', label: 'Unknown' },
};

export default function ClientDeletedDataTab({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [filterEntityType, setFilterEntityType] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState(null);
  const [snapshotTarget, setSnapshotTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listDeletedData(clientId, {
        entityType: filterEntityType || undefined,
        includeClientPermanentDeleted: true,
      });
      setItems(res.items || []);
    } catch (err) {
      setLoadError(translateError(err) || 'Failed to load deleted data');
    } finally {
      setLoading(false);
    }
  }, [clientId, filterEntityType]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  async function handleConfirmRestore() {
    if (!restoreTarget) return;
    setBusy(true);
    try {
      await restoreDeletedEntity(clientId, restoreTarget.deletionLogId);
      setRestoreTarget(null);
      await fetchAll();
    } catch (err) {
      alert(translateError(err) || 'Restore failed');
    } finally { setBusy(false); }
  }

  async function handleConfirmHardDelete() {
    if (!hardDeleteTarget) return;
    setBusy(true);
    try {
      await hardDeleteEntity(clientId, hardDeleteTarget.deletionLogId);
      setHardDeleteTarget(null);
      await fetchAll();
    } catch (err) {
      alert(translateError(err) || 'Hard delete failed');
    } finally { setBusy(false); }
  }

  const columns = [
    {
      key: 'entityType',
      label: 'Entity type',
      render: (row) => ENTITY_TYPE_LABEL[row.entityType] || row.entityType,
    },
    {
      key: 'entityId',
      label: 'ID',
      render: (row) => (
        <span className="cdd__id" title={row.entityId}>
          {String(row.entityId).slice(0, 8)}…
        </span>
      ),
    },
    {
      key: 'currentState',
      label: 'State',
      render: (row) => {
        const cfg = STATE_BADGE[row.currentState] || STATE_BADGE.unknown;
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      key: 'actedAt',
      label: 'Deleted at',
      render: (row) => formatDateTime(row.actedAt),
    },
    {
      key: 'actedBy',
      label: 'Deleted by',
      render: (row) => <span className="cdd__upn">{row.actedBy}</span>,
    },
    {
      key: 'reason',
      label: 'Reason',
      render: (row) => <span className="cdd__reason">{row.reason || '—'}</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="cdd__row-actions">
          {row.entitySnapshot && (
            <button
              type="button"
              className="cdd__action cdd__action--ghost"
              onClick={() => setSnapshotTarget(row)}
            >
              View snapshot
            </button>
          )}
          {row.currentState !== 'admin_hard_deleted' && row.currentState !== 'restored' && (
            <button
              type="button"
              className="cdd__action"
              onClick={() => setRestoreTarget(row)}
              disabled={busy}
            >
              Restore
            </button>
          )}
          {row.currentState !== 'admin_hard_deleted' && (
            <button
              type="button"
              className="cdd__action cdd__action--danger"
              onClick={() => setHardDeleteTarget(row)}
              disabled={busy}
            >
              Hard delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="cdd">
      <div className="cdd__toolbar">
        <label className="cdd__field">
          <span className="cdd__label">Entity type</span>
          <select
            className="cdd__select"
            value={filterEntityType}
            onChange={(e) => setFilterEntityType(e.target.value)}
          >
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <div className="cdd__hint">
          Includes both soft-deleted items (visible to the client in their Removed view) and
          client-permanent-deleted items (hidden from the client). Hard delete is irreversible.
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        rowKey={(row) => row.deletionLogId}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => {}}
        loading={loading}
        error={loadError}
        onRetry={fetchAll}
        emptyMessage="No deleted data for this Client."
      />

      <ConfirmDialog
        isOpen={Boolean(restoreTarget)}
        onClose={() => setRestoreTarget(null)}
        onConfirm={handleConfirmRestore}
        title="Restore deleted entity"
        confirmLabel={busy ? 'Restoring…' : 'Restore'}
        tone="primary"
        isSubmitting={busy}
        message={
          <>
            <p>
              Restore this {ENTITY_TYPE_LABEL[restoreTarget?.entityType] || restoreTarget?.entityType || 'entity'}?
            </p>
            <p>
              It will reappear in the client's live view. A new <code>restore</code> row will be appended to
              the DeletionLog audit trail.
            </p>
          </>
        }
      />

      <ConfirmDialog
        isOpen={Boolean(hardDeleteTarget)}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleConfirmHardDelete}
        title="Hard delete (irreversible)"
        confirmLabel={busy ? 'Deleting…' : 'Hard delete'}
        tone="danger"
        isSubmitting={busy}
        message={
          <>
            <p>
              <strong>This is irreversible.</strong> The {ENTITY_TYPE_LABEL[hardDeleteTarget?.entityType] || hardDeleteTarget?.entityType || 'entity'}{' '}
              row and all dependent rows (notes, attachments, action items, etc.) will be physically deleted from the database,
              and any associated blobs will be best-effort removed from storage.
            </p>
            <p>
              A final <code>admin_hard_delete</code> row will be appended to the DeletionLog audit trail. The entity itself cannot be restored after this point.
            </p>
          </>
        }
      />

      <JsonDialog
        isOpen={Boolean(snapshotTarget)}
        onClose={() => setSnapshotTarget(null)}
        title="Entity snapshot at deletion time"
        data={snapshotTarget?.entitySnapshot || null}
      />
    </div>
  );
}
